import { IQpDatatableFilterAndSortParam } from '@library/components/qp-datatable/qp-datatable.models';
import { BrandDTO } from '@library/dto/brand.dto';
import { DefectsChecklistGetDTO } from '@library/dto/checklist/defect/defects-checklist.dto';
import { InspectionTypeSettingDTO } from '@library/dto/inspection-type-settings.dto';
import { InspectionTypeDTO } from '@library/dto/inspection-type.dto';
import { PackingListSearchDTO } from '@library/dto/packing-list-search.dto';
import { PackingListDTO } from '@library/dto/packing-list.dto';
import { ProductDetailDTO } from '@library/dto/product-detail.dto';
import { PurchaseOrderViewDTO } from '@library/dto/purchase-order-view.dto';
import { ServiceProviderClientElasticDTO } from '@library/dto/service-provider-client-elastic.dto';
import { TestsChecklistDTO } from '@library/dto/tests-checklist.dto';
import { CustomFieldDTO } from '@library/dto/workflow/custom-fields.dto';
import { WorkflowTemplateGetDTO } from '@library/dto/workflow/workflow-template.dto';
import { InspectionStatus } from '@library/dto-enums/inspection-status.dto-enum';
import { QpIdType, QpUuidType } from '@library/models/qp-alias.models';
import { IQpConsultationPage } from '@library/models/qp-consultation-page.models';
import { EQpProfile } from '@library/models/qp-profile.models';
import { QpDateService } from '@library/services/qp-date/qp-date.service';
import { NetworkStatusService } from '@library/services/qp-network-status/qp-network-status.service';
import { IPurchaseOrderInfo } from '@one/app/pages/brd/pages/product-po/pages/po/models/purchase-orders.model';
import {
  BrdWorkflowChecklistConsultationType,
  EBrdWorkflowChecklistConsultationType,
} from '@one/app/pages/brd/pages/workflow/pages/checklist/pages/consult/brd-workflow-checklist-consult.models';
import { IWorkflowConsultation } from '@one/app/pages/brd/pages/workflow/pages/consult/brd-workflow-consult.models';
import { IBrdProductConsultation } from '@one/app/pages/brd/shared/models/brd-product-consult.model';
import { IEntityConsultation } from '@one/app/pages/brd/shared/models/entity.model';
import { IHistory } from '@one/app/pages/brd/shared/models/history.models';
import { DefectsChecklistService } from '@one/app/pages/brd/shared/services/defects-checklist.service';
import { EntityConsultationService } from '@one/app/pages/brd/shared/services/entity-consultation.service';
import { IDefectsChecklistDefect } from '@one/app/pages/isp/pages/inspection/pages/id/pages/defects-checklist/isp-inspection-id-defect.models';
import { QimaPublicDefectsService } from '@one/app/pages/isp/pages/inspection/pages/id/pages/defects-checklist/shared/services/qima-public-defects.service';
import { EInspectionDocumentType } from '@one/app/pages/isp/pages/inspection/pages/id/shared/components/instruction-documents/instruction-documents.models';
import { InstructionDocumentsService } from '@one/app/pages/isp/pages/inspection/pages/id/shared/components/instruction-documents/instruction-documents.service';
import WorkflowUtils from '@one/app/pages/isp/pages/inspection/pages/id/shared/services/workflow.utils';
import { HTTP_SKIP_409_OPTIONS } from '@one/app/shared/constants/http-skip-409-options';
import { IAccount } from '@one/app/shared/models/account/account.models';
import { IWorkflow } from '@one/app/shared/models/workflow/workflow.models';
import {
  EWorkflowTemplateStepActionType,
  EWorkflowTemplateStepName,
  IWorkflowTemplateStepAction,
} from '@one/app/shared/models/workflows/workflows-templates.models';
import { AccountService } from '@one/app/shared/services/account/account.service';
import { BrandService } from '@one/app/shared/services/brand/brand.service';
import { TestsChecklistService } from '@one/app/shared/services/checklist/tests-checklist.service';
import { CustomFieldsService } from '@one/app/shared/services/custom-fields/custom-fields.service';
import { DatabaseDataService } from '@one/app/shared/services/database/database-data.service';
import { InspectionService } from '@one/app/shared/services/inspection/inspection.service';
import { SettingsInspectionTypesService } from '@one/app/shared/services/inspection/settings-inspection-types.service';
import { PackingListService } from '@one/app/shared/services/packing-list/packing-list.service';
import { ProductService } from '@one/app/shared/services/product/product.service';
import { PurchaseOrderService } from '@one/app/shared/services/purchase-order/purchase-order.service';
import { ServiceProviderClientService } from '@one/app/shared/services/service-provider-client.service';
import { WorkflowTemplateService } from '@one/app/shared/services/workflow-template/workflow-template.service';
import { isMultiInspectorAssignment } from '@one/app/shared/utils/multi-inspector.utils';
import { Injectable } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { QimaOptionalType } from '@qima/ngx-qima';
import { isNil, isObject, isNumber } from 'lodash/index';
import { Observable, of, combineLatest, BehaviorSubject, forkJoin, from } from 'rxjs';
import { switchMap, map, filter, take, finalize, tap, catchError, defaultIfEmpty, distinctUntilChanged } from 'rxjs/operators';

const MAX_ITEMS_TO_PRELOAD = 100;

type IdLastModifiedDateMapType = Map<number, string>;

interface PreloadItemState {
  items: number;
  preloadedItems: number;
}

interface PreloadItem {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
  id: number;
  lastModifiedDate?: string | Date;
  history?: IHistory;
}

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class IspInspectionPreloadService {
  public readonly LOCAL_STORAGE_OFFLINE_KEY = 'isStartOfflineFeatureEnabled';
  public readonly LOCAL_STORAGE_LAST_PRELOADING_TIME_KEY = 'lastPreloadingTime';
  // Assigned in the constructor (not inline) so it can safely combine the private _isBulkPreloading$ and
  // _inviteePreloadsInFlight$ subjects while still being declared before them (member-ordering).
  public readonly isPreloading$: Observable<boolean>;
  public preloadState$ = new BehaviorSubject<PreloadState>({
    [PreloadKey.PRODUCT]: {
      items: 0,
      preloadedItems: 0,
    },
    [PreloadKey.PO]: {
      items: 0,
      preloadedItems: 0,
    },
    [PreloadKey.INSPECTION_TYPE]: {
      items: 0,
      preloadedItems: 0,
    },
    [PreloadKey.INSPECTION_TYPE_SETTING]: {
      items: 0,
      preloadedItems: 0,
    },
    [PreloadKey.WORKFLOW]: {
      items: 0,
      preloadedItems: 0,
    },
    [PreloadKey.TESTS_CHECKLIST]: {
      items: 0,
      preloadedItems: 0,
    },
    [PreloadKey.DEFECTS_CHECKLIST]: {
      items: 0,
      preloadedItems: 0,
    },
    [PreloadKey.CLIENT]: {
      items: 0,
      preloadedItems: 0,
    },
    [PreloadKey.ENTITY]: {
      items: 0,
      preloadedItems: 0,
    },
    [PreloadKey.PACKING_LIST]: {
      items: 0,
      preloadedItems: 0,
    },
  });

  private readonly _isBulkPreloading$ = new BehaviorSubject<boolean>(false);
  // Tracks in-flight invitee preloads (preloadInspectionForInvitee) so that consumers waiting on isPreloading$
  // (e.g. "no more network activity" indicators) also account for co-inspector background preloads, not only
  // the bulk _preloadData$ pass.
  private readonly _inviteePreloadsInFlight$ = new BehaviorSubject<number>(0);

  private readonly _tomorowDateParams = this._qpDateService.getDateWithoutTimezoneConvert(
    this._qpDateService.addDays(QpDateService.now, 1)
  );

  private readonly _oneMonthAgoDateParams = this._qpDateService.getDateWithoutTimezoneConvert(
    this._qpDateService.subtractDays(QpDateService.now, 30)
  );

  private readonly _getOneMonthFromNow = this._qpDateService.getDateWithoutTimezoneConvert(
    this._qpDateService.addDays(QpDateService.now, 30)
  );

  private readonly _defaultEntityPageSize = 50;
  private readonly _lastSynchronizationBehaviorSubject = new BehaviorSubject<string>('');

  public constructor(
    private readonly _inspectionService: InspectionService,
    private readonly _settingsInspectionTypesService: SettingsInspectionTypesService,
    private readonly _instructionDocuments: InstructionDocumentsService,
    private readonly _entityConsultationService: EntityConsultationService,
    private readonly _databaseDataService: DatabaseDataService,
    private readonly _productService: ProductService,
    private readonly _purchaseOrderService: PurchaseOrderService,
    private readonly _accountService: AccountService,
    private readonly _networkStatusService: NetworkStatusService,
    private readonly _qpDateService: QpDateService,
    private readonly _translateService: TranslateService,
    private readonly _workflowTemplateService: WorkflowTemplateService,
    private readonly _defectsChecklistService: DefectsChecklistService,
    private readonly _testsChecklistService: TestsChecklistService,
    private readonly _customFieldService: CustomFieldsService,
    private readonly _serviceProviderClientService: ServiceProviderClientService,
    private readonly _brandService: BrandService,
    private readonly _packingListService: PackingListService,
    private readonly _qimaPublicDefectsService: QimaPublicDefectsService
  ) {
    this.isPreloading$ = combineLatest([this._isBulkPreloading$, this._inviteePreloadsInFlight$]).pipe(
      map(([isBulkPreloading, inviteePreloadsInFlight]): boolean => isBulkPreloading || inviteePreloadsInFlight > 0),
      distinctUntilChanged()
    );

    this._databaseDataService.isDBInit$
      .pipe(
        filter((isDBInit: boolean): boolean => isDBInit),
        take(1),
        switchMap((): Observable<void> => this._preloadInspectionDataIfApplicable$())
      )
      .subscribe();

    this._getlastSynchronization$()
      .pipe(take(1), untilDestroyed(this))
      .subscribe((lastSynchronization: string): void => {
        this._lastSynchronizationBehaviorSubject.next(lastSynchronization);
      });
  }

  public get lastSynchronization$(): Observable<string> {
    return this._lastSynchronizationBehaviorSubject.pipe(switchMap((): Observable<string> => this._getlastSynchronization$()));
  }

  public preloadDocuments(workflow: IWorkflow, inspectionId: QpIdType | QpUuidType): void {
    const testFileList = WorkflowUtils.getTestFileListByWorkflow(workflow);
    const defectFileList = WorkflowUtils.getDefectFileListByWorkflow(workflow);
    const documentFileList = WorkflowUtils.getDocumentFileListByWorkflow(workflow);
    const publicDefectFileList = WorkflowUtils.getPublicDefectFileListByWorkflow(workflow);

    testFileList.forEach((checklistId: number, fileId: string): void => {
      this._preloadFileIfNotCached$(fileId, checklistId, EInspectionDocumentType.TEST);
    });

    defectFileList.forEach((checklistId: number, fileId: string): void => {
      this._preloadFileIfNotCached$(fileId, checklistId, EInspectionDocumentType.DEFECT);
    });

    documentFileList.forEach((fileId: string): void => {
      this._preloadFileIfNotCached$(fileId, inspectionId, EInspectionDocumentType.GLOBAL);
    });

    this._preloadPublicDefectDocumentsIfAllowed$(workflow, publicDefectFileList);
  }

  public manualAskForPreload$(): Observable<void> {
    return this._preloadInspectionDataIfApplicable$();
  }

  public preloadInspectionDataOnAccept(inspectionId: QpIdType | QpUuidType, inspectionStatus: InspectionStatus): void {
    if (inspectionStatus === InspectionStatus.ACCEPTED) {
      this._preloadInspectionWorkflowAndDocuments$(inspectionId).pipe(untilDestroyed(this)).subscribe();
    }
  }

  public preloadInspectionOnAccept(inspectionId: QpIdType | QpUuidType, inspectionStatus: InspectionStatus): void {
    if (inspectionStatus === InspectionStatus.ACCEPTED) {
      this._preloadInspectionEntity$(inspectionId).pipe(untilDestroyed(this)).subscribe();
    }
  }

  /**
   * @description
   * Multi-inspector invitee preload.
   *
   * Decouples inspection-specific preload from the local accept action so that ANY invited inspector gets the
   * inspection entity, workflow and documents cached as soon as the assignment is ACCEPTED or STARTED — not only
   * the device that clicked "accept". This does not depend on the offline settings toggle; it only prepares data
   * for co-inspectors (including later offline use).
   *
   * Enforces the "multi-inspector only" contract itself via {@link isMultiInspectorAssignment}, so callers don't
   * need to remember to gate on participants/inspectors before calling this public method. Does not depend on
   * the offline settings toggle (see class doc), but still requires an actual network connection to preload.
   * @param {QpIdType | QpUuidType} inspectionId The inspection to preload.
   * @param {InspectionStatus | null} [inspectionStatus] The inspection status (ACCEPTED or STARTED trigger a preload).
   * @param {readonly unknown[] | null} [participants] The inspection's participants/inspectors, used to confirm this is a shared assignment.
   */
  public preloadInspectionForInvitee(
    inspectionId: QpIdType | QpUuidType,
    inspectionStatus: InspectionStatus | null | undefined,
    participants: readonly unknown[] | null | undefined
  ): void {
    if (
      !this._networkStatusService.isOnline ||
      !isMultiInspectorAssignment(participants) ||
      (inspectionStatus !== InspectionStatus.ACCEPTED && inspectionStatus !== InspectionStatus.STARTED)
    ) {
      return;
    }

    this._trackInviteePreload$(
      forkJoin([this._preloadInspectionEntity$(inspectionId), this._preloadInspectionWorkflowAndDocuments$(inspectionId)]).pipe(
        map((): void => void 0)
      )
    )
      .pipe(untilDestroyed(this))
      .subscribe();
  }

  /**
   * @description
   * Biotech offline preload for single-inspector assignments that never go through Accept.
   * Uses the same entity/workflow/document download as {@link preloadInspectionForInvitee} without the
   * multi-inspector gate or the offline-settings toggle (still requires network).
   */
  public preloadBiotechInspection(inspectionId: QpIdType | QpUuidType, inspectionStatus: InspectionStatus | null | undefined): void {
    if (
      !this._networkStatusService.isOnline ||
      (inspectionStatus !== InspectionStatus.ACCEPTED && inspectionStatus !== InspectionStatus.STARTED)
    ) {
      return;
    }

    this._trackInviteePreload$(
      forkJoin([this._preloadInspectionEntity$(inspectionId), this._preloadInspectionWorkflowAndDocuments$(inspectionId)]).pipe(
        map((): void => void 0)
      )
    )
      .pipe(untilDestroyed(this))
      .subscribe();
  }

  public restorePreloadState(): void {
    Object.keys(PreloadKey).forEach((key): void =>
      this._updatePreloadState(PreloadKey[key], {
        preloadedItems: 0,
        items: 0,
      })
    );
  }

  public preloadPOAndProductIfNotInDatabase$(productId: number, purchaseOrderId: number | undefined): Observable<void> {
    return combineLatest([
      isNil(purchaseOrderId) ? of(undefined) : from(this._databaseDataService.getPurchaseOrder(purchaseOrderId)),
      from(this._databaseDataService.getProduct(productId)),
    ]).pipe(
      take(1),
      switchMap(([po, product]: [PurchaseOrderViewDTO | undefined, ProductDetailDTO | undefined]): Observable<void> => {
        const operations: Observable<unknown>[] = [];

        if (isNil(po) && !isNil(purchaseOrderId)) {
          operations.push(
            this._purchaseOrderService
              .getPurchaseOrder$(purchaseOrderId)
              .pipe(
                switchMap(
                  (purchaseOrder: PurchaseOrderViewDTO): Observable<PurchaseOrderViewDTO | undefined> =>
                    from(this._databaseDataService.savePurchaseOrder(purchaseOrder))
                )
              )
          );
        }

        if (isNil(product)) {
          operations.push(
            this._productService
              .getProductDTOById$(productId)
              .pipe(switchMap((p: ProductDetailDTO): Observable<ProductDetailDTO> => from(this._databaseDataService.saveProduct(p))))
          );
        }

        if (operations.length === 0) {
          return of(void 0);
        }

        return forkJoin(operations).pipe(map((): void => void 0));
      })
    );
  }

  public preloadWorkflowAndChecklistsIfNotInDatabase$(workflowId: number): Observable<void> {
    return from(this._databaseDataService.getWorkflowTemplateById(workflowId)).pipe(
      switchMap((workflow: QimaOptionalType<WorkflowTemplateGetDTO>): Observable<WorkflowTemplateGetDTO> => {
        if (workflow) {
          return of(workflow);
        }

        return this._workflowTemplateService
          .getWorkflowTemplate$(workflowId)
          .pipe(
            switchMap(
              (wf: WorkflowTemplateGetDTO): Observable<WorkflowTemplateGetDTO> => from(this._databaseDataService.saveWorkflowTemplate(wf))
            )
          );
      }),
      switchMap((workflow: WorkflowTemplateGetDTO): Observable<void> => {
        const duringStep = workflow.content.steps.find((s): boolean => s.name === EWorkflowTemplateStepName.DURING_INSPECTION);

        if (isNil(duringStep)) {
          return of(void 0);
        }

        const actions = (duringStep.actions ?? []) as IWorkflowTemplateStepAction[];
        const testsChecklistIds: number[] = actions
          .filter((a): boolean => a.type === EWorkflowTemplateStepActionType.TESTS_CHECKLIST && !!a.testsChecklistId)
          .map((a): number => a.testsChecklistId as number);
        const defectsChecklistIds: number[] = actions
          .filter((a): boolean => a.type === EWorkflowTemplateStepActionType.DEFECTS_CHECKLIST && !!a.defectsChecklistId)
          .map((a): number => a.defectsChecklistId as number);
        const operations: Observable<unknown>[] = [];

        testsChecklistIds.forEach((checklistId: number): void => {
          operations.push(
            from(this._databaseDataService.getTestsChecklistById(checklistId)).pipe(
              take(1),
              switchMap((existing): Observable<unknown> => {
                if (existing) {
                  return of(void 0);
                }

                return this._testsChecklistService
                  .getTestsChecklistDTO$(checklistId)
                  .pipe(switchMap((checklist): Observable<unknown> => from(this._databaseDataService.saveTestsChecklist(checklist))));
              })
            )
          );
        });

        defectsChecklistIds.forEach((checklistId: number): void => {
          operations.push(
            from(this._databaseDataService.getDefectsChecklistById(checklistId)).pipe(
              take(1),
              switchMap((existing): Observable<unknown> => {
                if (existing) {
                  return of(void 0);
                }

                return this._defectsChecklistService
                  .getDefectChecklist$(checklistId.toString())
                  .pipe(switchMap((checklist): Observable<unknown> => from(this._databaseDataService.saveDefectsChecklist(checklist))));
              })
            )
          );
        });

        if (operations.length === 0) {
          return of(void 0);
        }

        return forkJoin(operations).pipe(map((): void => void 0));
      })
    );
  }

  /**
   * Preloads attachment binary + metadata for the given defects while online.
   * Called from IspInspectionIdComponent after prefetching the public defect library.
   * @param defects
   * @param networkOnline
   */
  public schedulePublicDefectAttachmentPreloadForDefects(defects: readonly IDefectsChecklistDefect[], networkOnline: boolean): void {
    if (!networkOnline || defects.length === 0) {
      return;
    }

    this._preloadPublicDefectAttachmentsForDefects$([...defects])
      .pipe(
        take(1),
        catchError((): Observable<void> => of(void 0))
      )
      .subscribe();
  }

  /**
   * Increments/decrements the invitee-preload-in-flight counter around the given observable so that
   * {@link isPreloading$} reflects co-inspector background preloads too, not only the bulk _preloadData$ pass.
   * @param source$
   */
  private _trackInviteePreload$<T>(source$: Observable<T>): Observable<T> {
    this._inviteePreloadsInFlight$.next(this._inviteePreloadsInFlight$.value + 1);

    return source$.pipe(
      finalize((): void => {
        this._inviteePreloadsInFlight$.next(Math.max(0, this._inviteePreloadsInFlight$.value - 1));
      })
    );
  }

  /**
   * Fetches the inspection so the inspection interceptor caches it in the local database for offline use.
   * @param inspectionId
   */
  private _preloadInspectionEntity$(inspectionId: QpIdType | QpUuidType): Observable<void> {
    return this._inspectionService.getInspection$(inspectionId).pipe(
      take(1),
      map((): void => void 0),
      catchError((): Observable<void> => of(void 0))
    );
  }

  private _preloadInspectionWorkflowAndDocuments$(inspectionId: QpIdType | QpUuidType): Observable<void> {
    return this._inspectionService.getWorkflow$(inspectionId, true, HTTP_SKIP_409_OPTIONS).pipe(
      take(1),
      tap((workflow: QimaOptionalType<IWorkflow>): void => {
        if (!isNil(workflow)) {
          this.preloadDocuments(workflow, inspectionId);
        }
      }),
      map((): void => void 0),
      catchError((): Observable<void> => of(void 0))
    );
  }

  /**
   * Preloads public-defect attachments only when brand and workflow allow the QIMA public defects
   * database (same rule as inspection-id public-defect prefetch). Avoids noisy calls to the
   * public-defects API when the feature is off.
   * @param workflow
   * @param publicDefectFileList
   */
  private _preloadPublicDefectDocumentsIfAllowed$(
    workflow: IWorkflow,
    publicDefectFileList: { documentId: string; defectId: string }[]
  ): void {
    if (publicDefectFileList.length === 0) {
      return;
    }

    const workflowAllowsPublicDefects = workflow.settings?.allowQimaPublicDefectsDatabase ?? false;

    this._accountService
      .getBrand$()
      .pipe(
        take(1),
        map((brand): boolean => (brand?.allowQimaPublicDefectsDatabase ?? false) && workflowAllowsPublicDefects),
        defaultIfEmpty(false),
        catchError((): Observable<boolean> => of(false)),
        untilDestroyed(this)
      )
      .subscribe((allows: boolean): void => {
        if (!allows) {
          return;
        }

        publicDefectFileList.forEach(({ documentId, defectId }: { documentId: string; defectId: string }): void => {
          this._preloadFileIfNotCached$(documentId, defectId, EInspectionDocumentType.PUBLIC_DEFECT);
        });
      });
  }

  /**
   * Check if file is already cached with metadata before downloading.
   * For PUBLIC_DEFECT, the interceptor uses cache key `public-defect-${defectId}-${documentId}`.
   * @param fileId
   * @param entityId
   * @param documentType
   */
  private _preloadFileIfNotCached$(fileId: string, entityId: QpIdType | QpUuidType, documentType: EInspectionDocumentType): void {
    const cacheKey = documentType === EInspectionDocumentType.PUBLIC_DEFECT ? `public-defect-${entityId}-${fileId}` : fileId;

    from(this._databaseDataService.getFile(cacheKey))
      .pipe(
        switchMap((cachedFile): Observable<void> => {
          // Skip if file already has metadata cached
          if (cachedFile?.data && 'name' in cachedFile.data && cachedFile.data.name) {
            return of(void 0);
          }

          // File not cached or missing metadata - download it
          const document$ =
            documentType === EInspectionDocumentType.GLOBAL
              ? this._inspectionService.getDocument$(entityId, fileId)
              : this._instructionDocuments.getInstructionDocumentByInspection(entityId, fileId, false, documentType);

          return document$.pipe(
            switchMap(
              (): Observable<{ name: string; type: string; size: string } | null> =>
                this._instructionDocuments.getInstructionDocumentMetadataByInspection$(entityId, fileId, documentType)
            ),
            map((): void => void 0)
          );
        }),
        untilDestroyed(this)
      )
      .subscribe();
  }

  private _preloadInspectionDataIfApplicable$(): Observable<void> {
    if (localStorage.getItem(this.LOCAL_STORAGE_OFFLINE_KEY) !== 'true') {
      this._isBulkPreloading$.next(false);

      return of(void 0);
    }

    return this._accountService.getAccount$().pipe(
      filter(
        (account: QimaOptionalType<IAccount>): account is IAccount =>
          account?.profiles.includes(EQpProfile.ROLE_SERVICE_PROVIDER_INSPECTOR) ?? false
      ),
      take(1),
      switchMap((account): Observable<void> => this._preloadData$(account))
    );
  }

  private _preloadData$(account: IAccount): Observable<void> {
    this._isBulkPreloading$.next(true);

    return forkJoin([
      this._preloadProductsAndPOs$().pipe(this._endPreloadOperation$),
      this._preloadWorkflows$().pipe(this._endPreloadOperation$),
      this._preloadDefectsChecklist$().pipe(this._endPreloadOperation$),
      this._preloadTestsChecklist$().pipe(this._endPreloadOperation$),
      this._preloadInspectionTypes$().pipe(this._endPreloadOperation$),
      this._preloadInspectionTypeSettings$(account.brandId).pipe(this._endPreloadOperation$),
      this._preloadCustomFields$().pipe(this._endPreloadOperation$),
      this._preloadCliends$(account.brandId).pipe(this._endPreloadOperation$),
      this._preloadEntities$().pipe(this._endPreloadOperation$),
      this._preloadPackingLists$().pipe(this._endPreloadOperation$),
      this._preloadPublicDefectAttachments$().pipe(this._endPreloadOperation$),
    ]).pipe(
      finalize((): void => {
        this._isBulkPreloading$.next(false);
        this._updateLastSync();
      }),
      map((): void => {
        return void 0;
      })
    );
  }

  // CLIENTS 👩‍🚀

  private _preloadCliends$(inspectorBrandId: string | undefined): Observable<void> {
    return this._serviceProviderClientService.getClients$().pipe(
      take(1),
      switchMap((clients: ServiceProviderClientElasticDTO[]): Observable<void[]> => {
        this._updatePreloadState(PreloadKey.CLIENT, {
          items: clients.length + 1, // +1 for the inspector brand
          preloadedItems: 0,
        });

        const clientsMap: IdLastModifiedDateMapType = new Map();

        clients.forEach((client: ServiceProviderClientElasticDTO): void => {
          clientsMap.set(client.clientId, client.lastModifiedDate ?? new Date().toISOString());
        });

        if (!isNil(inspectorBrandId)) {
          clientsMap.set(parseInt(inspectorBrandId, 10), new Date().toISOString());
        }

        return combineLatest(
          Array.from(clientsMap).map(([id, lastModifiedDate]): Observable<void> => this._preloadClientsIfNotUpToDate$(id, lastModifiedDate))
        );
      }),
      map((): void => void 0)
    );
  }

  private _preloadClientsIfNotUpToDate$(id: number, lastModifiedDate: string): Observable<void> {
    return from(this._databaseDataService.getBrandById(id)).pipe(
      filter((client: BrandDTO | undefined): client is BrandDTO => {
        const isNeedToBePreload = !!(isNil(client) || client.lastModifiedDate !== lastModifiedDate);

        if (!isNeedToBePreload) {
          this._updatePreloadState(PreloadKey.CLIENT, {
            preloadedItems: this.preloadState$.value.client.preloadedItems + 1,
          });
        }

        return isNeedToBePreload;
      }),
      switchMap((): Observable<BrandDTO> => this._brandService.getBrand$(id)),
      switchMap((brand: BrandDTO): Observable<BrandDTO | undefined> => {
        this._updatePreloadState(PreloadKey.CLIENT, {
          preloadedItems: this.preloadState$.value.client.preloadedItems + 1,
        });

        return from(this._databaseDataService.saveBrand(brand));
      }),
      map((): void => void 0)
    );
  }

  // CUSTOM FIELDS 🎨

  private _preloadCustomFields$(): Observable<void> {
    return this._customFieldService.getClientsCustomFields$().pipe(
      switchMap((customFields: CustomFieldDTO[]): Observable<CustomFieldDTO[]> => {
        if (customFields.length === 0) {
          return of([]);
        }

        return combineLatest(
          customFields.map((customField: CustomFieldDTO): Observable<CustomFieldDTO> => {
            return from(this._databaseDataService.saveCustomField(customField));
          })
        );
      }),
      map((): void => void 0)
    );
  }

  // DEFECTS CHECKLISTS 🐛

  private _preloadDefectsChecklist$(): Observable<void> {
    return this._getChecklistsIdLastModifiedDateMap$(EBrdWorkflowChecklistConsultationType.DEFECTS).pipe(
      take(1),
      switchMap((defectsChecklistsMap: IdLastModifiedDateMapType): Observable<void[]> => {
        this._updatePreloadState(PreloadKey.DEFECTS_CHECKLIST, {
          items: defectsChecklistsMap.size,
          preloadedItems: 0,
        });

        return combineLatest(
          Array.from(defectsChecklistsMap).map(
            ([id, lastModifiedDate]): Observable<void> => this._preloadDefectsChecklistIfNotUpToDate$(id, lastModifiedDate)
          )
        );
      }),
      map((): void => void 0)
    );
  }

  private _preloadDefectsChecklistIfNotUpToDate$(checklistId: number, lastModifiedDate: string): Observable<void> {
    return from(this._databaseDataService.getDefectsChecklistById(checklistId)).pipe(
      switchMap((cachedChecklist: DefectsChecklistGetDTO | undefined): Observable<DefectsChecklistGetDTO> => {
        const isNeedToBePreload = isNil(cachedChecklist) || cachedChecklist.lastModifiedDate !== lastModifiedDate;

        if (isNeedToBePreload) {
          // Fetch from API and save to DB
          return this._defectsChecklistService.getDefectChecklist$(checklistId.toString()).pipe(
            switchMap((checklist: DefectsChecklistGetDTO): Observable<DefectsChecklistGetDTO> => {
              this._updatePreloadState(PreloadKey.DEFECTS_CHECKLIST, {
                preloadedItems: this.preloadState$.value.defectsChecklist.preloadedItems + 1,
              });

              return from(this._databaseDataService.saveDefectsChecklist(checklist)).pipe(map((): DefectsChecklistGetDTO => checklist));
            })
          );
        }

        // Checklist is up-to-date, use cached version
        this._updatePreloadState(PreloadKey.DEFECTS_CHECKLIST, {
          preloadedItems: this.preloadState$.value.defectsChecklist.preloadedItems + 1,
        });

        return of(cachedChecklist);
      }),
      // Always preload files regardless of whether checklist was cached or fetched
      switchMap((checklist: DefectsChecklistGetDTO): Observable<void> => this._preloadDefectsChecklistFiles$(checklist)),
      map((): void => void 0)
    );
  }

  private _preloadDefectsChecklistFiles$(checklist: DefectsChecklistGetDTO): Observable<void> {
    const fileIds: string[] = [];

    // Extract all document IDs from defects
    checklist.categories?.forEach((category): void => {
      category.defects?.forEach((defect): void => {
        if (defect.documents?.length) {
          fileIds.push(...defect.documents);
        }
      });
    });

    if (fileIds.length === 0) {
      return of(void 0);
    }

    // Preload each file and its metadata
    const preloadObservables = fileIds.map(
      (fileId: string): Observable<void> =>
        this._instructionDocuments.getInstructionDocumentByInspection(checklist.id, fileId, false, EInspectionDocumentType.DEFECT).pipe(
          switchMap(
            (): Observable<{ name: string; type: string; size: string } | null> =>
              this._instructionDocuments.getInstructionDocumentMetadataByInspection$(checklist.id, fileId, EInspectionDocumentType.DEFECT)
          ),
          map((): void => void 0),
          catchError((): Observable<void> => of(void 0))
        )
    );

    return forkJoin(preloadObservables).pipe(
      map((): void => void 0),
      defaultIfEmpty(void 0)
    );
  }

  // TESTS CHECKLIST 📝

  private _preloadTestsChecklist$(): Observable<void> {
    return this._getChecklistsIdLastModifiedDateMap$(EBrdWorkflowChecklistConsultationType.TESTS).pipe(
      take(1),
      switchMap((testsChecklistsMap: IdLastModifiedDateMapType): Observable<void[]> => {
        this._updatePreloadState(PreloadKey.TESTS_CHECKLIST, {
          items: testsChecklistsMap.size,
          preloadedItems: 0,
        });

        return combineLatest(
          Array.from(testsChecklistsMap).map(
            ([id, lastModifiedDate]): Observable<void> => this._preloadTestsChecklistIfNotUpToDate$(id, lastModifiedDate)
          )
        );
      }),
      map((): void => void 0)
    );
  }

  private _preloadTestsChecklistIfNotUpToDate$(checklistId: number, lastModifiedDate: string): Observable<void> {
    return from(this._databaseDataService.getTestsChecklistById(checklistId)).pipe(
      switchMap((cachedChecklist: TestsChecklistDTO | undefined): Observable<TestsChecklistDTO> => {
        const isNeedToBePreload = isNil(cachedChecklist) || cachedChecklist.lastModifiedDate !== lastModifiedDate;

        if (isNeedToBePreload) {
          // Fetch from API and save to DB
          return this._testsChecklistService.getTestsChecklistDTO$(checklistId).pipe(
            switchMap((checklist: TestsChecklistDTO): Observable<TestsChecklistDTO> => {
              this._updatePreloadState(PreloadKey.TESTS_CHECKLIST, {
                preloadedItems: this.preloadState$.value.testsChecklist.preloadedItems + 1,
              });

              return from(this._databaseDataService.saveTestsChecklist(checklist)).pipe(map((): TestsChecklistDTO => checklist));
            })
          );
        }

        // Checklist is up-to-date, use cached version
        this._updatePreloadState(PreloadKey.TESTS_CHECKLIST, {
          preloadedItems: this.preloadState$.value.testsChecklist.preloadedItems + 1,
        });

        return of(cachedChecklist);
      }),
      // Always preload files regardless of whether checklist was cached or fetched
      switchMap((checklist: TestsChecklistDTO): Observable<void> => this._preloadTestsChecklistFiles$(checklist)),
      map((): void => void 0)
    );
  }

  private _preloadTestsChecklistFiles$(checklist: TestsChecklistDTO): Observable<void> {
    const fileIds: string[] = [];

    // Extract all instructionsDocuments from test checklist elements
    checklist.content?.elements?.forEach((div): void => {
      div.elements?.forEach((question): void => {
        if (question.instructionsDocuments?.length) {
          fileIds.push(...question.instructionsDocuments);
        }
      });
    });

    if (fileIds.length === 0) {
      return of(void 0);
    }

    // Preload each file and its metadata
    const preloadObservables = fileIds.map(
      (fileId: string): Observable<void> =>
        this._instructionDocuments.getInstructionDocumentByInspection(checklist.id, fileId, false, EInspectionDocumentType.TEST).pipe(
          switchMap(
            (): Observable<{ name: string; type: string; size: string } | null> =>
              this._instructionDocuments.getInstructionDocumentMetadataByInspection$(checklist.id, fileId, EInspectionDocumentType.TEST)
          ),
          map((): void => void 0),
          catchError((): Observable<void> => of(void 0))
        )
    );

    return forkJoin(preloadObservables).pipe(
      map((): void => void 0),
      defaultIfEmpty(void 0)
    );
  }

  private _preloadPublicDefectAttachments$(): Observable<void> {
    return this._accountService.getBrand$().pipe(
      take(1),
      map((brand): boolean => brand?.allowQimaPublicDefectsDatabase ?? false),
      defaultIfEmpty(false),
      catchError((): Observable<boolean> => of(false)),
      switchMap((brandAllowsPublicDefects: boolean): Observable<void> => {
        if (!brandAllowsPublicDefects) {
          return of(void 0);
        }

        return this._qimaPublicDefectsService.refreshCacheIfNeeded$().pipe(
          switchMap((defects: IDefectsChecklistDefect[]): Observable<void> => this._preloadPublicDefectAttachmentsForDefects$(defects)),
          catchError((): Observable<void> => of(void 0))
        );
      })
    );
  }

  private _preloadPublicDefectAttachmentsForDefects$(defects: IDefectsChecklistDefect[]): Observable<void> {
    const preloadObservables: Observable<void>[] = [];

    defects.forEach((defect: IDefectsChecklistDefect): void => {
      const defectId = defect.qspDefectId;

      if (defectId && defect.documents?.length) {
        defect.documents.forEach((docId: string): void => {
          preloadObservables.push(this._preloadPublicDefectFile$(defectId, docId));
        });
      }
    });

    if (preloadObservables.length === 0) {
      return of(void 0);
    }

    return forkJoin(preloadObservables).pipe(
      map((): void => void 0),
      defaultIfEmpty(void 0)
    );
  }

  private _preloadPublicDefectFile$(defectId: string, documentId: string): Observable<void> {
    const cacheKey = `public-defect-${defectId}-${documentId}`;

    return from(this._databaseDataService.getFile(cacheKey)).pipe(
      switchMap((cachedFile): Observable<void> => {
        if (cachedFile?.data && 'name' in cachedFile.data && cachedFile.data.name) {
          return of(void 0);
        }

        return this._instructionDocuments
          .getInstructionDocumentByInspection(defectId, documentId, false, EInspectionDocumentType.PUBLIC_DEFECT)
          .pipe(
            switchMap(
              (): Observable<{ name: string; type: string; size: string } | null> =>
                this._instructionDocuments.getInstructionDocumentMetadataByInspection$(
                  defectId,
                  documentId,
                  EInspectionDocumentType.PUBLIC_DEFECT
                )
            ),
            map((): void => void 0)
          );
      }),
      catchError((): Observable<void> => of(void 0))
    );
  }

  // COMMON CHECKLIST 📝

  private _getChecklistsIdLastModifiedDateMap$(type: EBrdWorkflowChecklistConsultationType): Observable<IdLastModifiedDateMapType> {
    return this._getEntityIdLastModifiedDateMap$(
      (pageQuery, filtersAndSorts): Observable<IQpConsultationPage<BrdWorkflowChecklistConsultationType>> =>
        this._entityConsultationService.getChecklists$(pageQuery, filtersAndSorts),
      [
        {
          type: 'filter',
          value: `type,EQUALS,${type}`,
        },
        { type: 'sort', value: 'lastModifiedDate,DESC' },
      ]
    );
  }

  // WORKFLOW 🏗️

  private _preloadWorkflows$(): Observable<void> {
    return this._getWorkflowIdLastModifiedDateMap$().pipe(
      take(1),
      switchMap((workflowIdsMap: IdLastModifiedDateMapType): Observable<void[]> => {
        this._updatePreloadState(PreloadKey.WORKFLOW, {
          items: workflowIdsMap.size,
          preloadedItems: 0,
        });

        return combineLatest(
          Array.from(workflowIdsMap).map(
            ([id, lastModifiedDate]): Observable<void> => this._preloadWorkflowIfNotUpToDate$(id, lastModifiedDate)
          )
        );
      }),
      map((): void => void 0)
    );
  }

  private _getWorkflowIdLastModifiedDateMap$(): Observable<IdLastModifiedDateMapType> {
    return this._getEntityIdLastModifiedDateMap$(
      (pageQuery, filtersAndSorts): Observable<IQpConsultationPage<IWorkflowConsultation>> =>
        this._entityConsultationService.getWorkflows$(pageQuery, filtersAndSorts),
      [{ type: 'sort', value: 'lastModifiedDate,DESC' }]
    );
  }

  private _preloadWorkflowIfNotUpToDate$(workflowId: number, lastModifiedDate: string): Observable<void> {
    return from(this._databaseDataService.getWorkflowTemplateById(workflowId)).pipe(
      filter((w: WorkflowTemplateGetDTO | undefined): w is WorkflowTemplateGetDTO => {
        const isNeedToBePreload = !!(isNil(w) || w.lastModifiedDate !== lastModifiedDate);

        if (!isNeedToBePreload) {
          this._updatePreloadState(PreloadKey.WORKFLOW, {
            preloadedItems: this.preloadState$.value.workflow.preloadedItems + 1,
          });
        }

        return isNeedToBePreload;
      }),
      switchMap((): Observable<WorkflowTemplateGetDTO> => this._workflowTemplateService.getWorkflowTemplate$(workflowId)),
      switchMap((w: WorkflowTemplateGetDTO): Observable<WorkflowTemplateGetDTO | undefined> => {
        this._updatePreloadState(PreloadKey.WORKFLOW, {
          preloadedItems: this.preloadState$.value.workflow.preloadedItems + 1,
        });

        return from(this._databaseDataService.saveWorkflowTemplate(w));
      }),
      map((): void => void 0)
    );
  }

  // INSPECTION TYPE 🌈

  private _preloadInspectionTypes$(): Observable<InspectionTypeDTO[]> {
    return this._inspectionService.getAllInspectionTypes$().pipe(
      take(1),
      switchMap((inspectionTypes: InspectionTypeDTO[]): Observable<InspectionTypeDTO[]> => {
        this._updatePreloadState(PreloadKey.INSPECTION_TYPE, {
          items: inspectionTypes.length,
          preloadedItems: 0,
        });

        return combineLatest(
          inspectionTypes.map((inspectionType: InspectionTypeDTO): Observable<InspectionTypeDTO> => {
            return from(this._databaseDataService.saveInspectionType(inspectionType)).pipe(
              tap((): void => {
                this._updatePreloadState(PreloadKey.INSPECTION_TYPE, {
                  preloadedItems: this.preloadState$.value.inspectionType.preloadedItems + 1,
                });
              })
            );
          })
        );
      })
    );
  }

  private _preloadInspectionTypeSettings$(brandId: string | undefined): Observable<InspectionTypeSettingDTO[]> {
    if (isNil(brandId)) {
      return of([]);
    }

    return this._settingsInspectionTypesService.getInspectionTypes$(brandId).pipe(
      take(1),
      switchMap((inspectionTypeSetting: InspectionTypeSettingDTO[]): Observable<InspectionTypeSettingDTO[]> => {
        this._updatePreloadState(PreloadKey.INSPECTION_TYPE_SETTING, {
          items: inspectionTypeSetting.length,
          preloadedItems: 0,
        });

        return combineLatest(
          inspectionTypeSetting.map((inspectionTypeSetting: InspectionTypeSettingDTO): Observable<InspectionTypeSettingDTO> => {
            return from(this._databaseDataService.saveInspectionTypeSettings(inspectionTypeSetting)).pipe(
              tap((): void => {
                this._updatePreloadState(PreloadKey.INSPECTION_TYPE_SETTING, {
                  preloadedItems: this.preloadState$.value.inspectionTypeSetting.preloadedItems + 1,
                });
              })
            );
          })
        );
      })
    );
  }

  // PRODUCT & PO 📦

  private _preloadProductsAndPOs$(): Observable<(ProductDetailDTO | PurchaseOrderViewDTO | undefined)[]> {
    return forkJoin([this._getProductsIdLastModifiedDateMap$(), this._getPOsIdLastModifiedDateMap$()]).pipe(
      switchMap(([productsMap, posMap]): Observable<(ProductDetailDTO | PurchaseOrderViewDTO | undefined)[]> => {
        this._updatePreloadState(PreloadKey.PRODUCT, {
          items: productsMap.size,
          preloadedItems: 0,
        });

        this._updatePreloadState(PreloadKey.PO, {
          items: posMap.size,
          preloadedItems: 0,
        });

        return combineLatest([
          ...Array.from(productsMap).map(
            ([id, lastModifiedDate]): Observable<ProductDetailDTO | undefined> => this._preloadProductIfNotUpToDate$(id, lastModifiedDate)
          ),
          ...Array.from(posMap).map(
            ([id, lastModifiedDate]): Observable<PurchaseOrderViewDTO | undefined> => this._preloadPOIfNotUpToDate$(id, lastModifiedDate)
          ),
        ]);
      })
    );
  }

  private _getProductsIdLastModifiedDateMap$(): Observable<IdLastModifiedDateMapType> {
    return this._getEntityIdLastModifiedDateMap$(
      (pageQuery, filtersAndSorts): Observable<IQpConsultationPage<IBrdProductConsultation>> =>
        this._entityConsultationService.getProducts$(pageQuery, filtersAndSorts),
      [{ type: 'sort', value: 'lastModifiedDate,DESC' }]
    );
  }

  private _getPOsIdLastModifiedDateMap$(): Observable<IdLastModifiedDateMapType> {
    return this._getEntityIdLastModifiedDateMap$(
      (pageQuery, filtersAndSorts): Observable<IQpConsultationPage<IPurchaseOrderInfo>> =>
        this._entityConsultationService.getPurchaseOrders$(pageQuery, filtersAndSorts),
      [
        {
          type: 'filter',
          value: `lastModifiedDate,BETWEEN,[${this._oneMonthAgoDateParams},${this._tomorowDateParams},${QpDateService.timezone}]`,
        },
        { type: 'sort', value: 'lastModifiedDate,DESC' },
      ]
    );
  }

  private _preloadProductIfNotUpToDate$(productId: number, lastModifiedDate: string): Observable<ProductDetailDTO | undefined> {
    return from(this._databaseDataService.getProduct(productId)).pipe(
      filter((product: ProductDetailDTO | undefined): product is ProductDetailDTO => {
        const isNeedToBePreload = !!(isNil(product) || product.lastModifiedDate !== lastModifiedDate);

        if (!isNeedToBePreload) {
          this._updatePreloadState(PreloadKey.PRODUCT, {
            preloadedItems: this.preloadState$.value.product.preloadedItems + 1,
          });
        }

        return isNeedToBePreload;
      }),
      switchMap((): Observable<ProductDetailDTO> => this._productService.getProductDTOById$(productId)),
      switchMap((product: ProductDetailDTO): Observable<ProductDetailDTO> => {
        this._updatePreloadState(PreloadKey.PRODUCT, {
          preloadedItems: this.preloadState$.value.product.preloadedItems + 1,
        });

        return from(this._databaseDataService.saveProduct(product));
      })
    );
  }

  private _preloadPOIfNotUpToDate$(poId: number, lastModifiedDate: string): Observable<PurchaseOrderViewDTO | undefined> {
    return from(this._databaseDataService.getPurchaseOrder(poId)).pipe(
      filter((po: PurchaseOrderViewDTO | undefined): po is PurchaseOrderViewDTO => {
        const isNeedToBePreload = !!(isNil(po) || po.lastModifiedDate !== lastModifiedDate);

        if (!isNeedToBePreload) {
          this._updatePreloadState(PreloadKey.PO, {
            preloadedItems: this.preloadState$.value.po.preloadedItems + 1,
          });
        }

        return isNeedToBePreload;
      }),
      switchMap((): Observable<PurchaseOrderViewDTO> => this._purchaseOrderService.getPurchaseOrder$(poId)),
      switchMap((po: PurchaseOrderViewDTO): Observable<PurchaseOrderViewDTO | undefined> => {
        this._updatePreloadState(PreloadKey.PO, {
          preloadedItems: this.preloadState$.value.po.preloadedItems + 1,
        });

        return from(this._databaseDataService.savePurchaseOrder(po));
      })
    );
  }

  // ENTITIES 🏢

  private _preloadEntities$(): Observable<void> {
    return this._getEntitiesIdLastModifiedDateMap$().pipe(
      take(1),
      switchMap((entitiesMap: IdLastModifiedDateMapType): Observable<void[]> => {
        this._updatePreloadState(PreloadKey.ENTITY, {
          items: entitiesMap.size,
          preloadedItems: 0,
        });

        if (entitiesMap.size === 0) {
          return of([]);
        }

        return combineLatest(
          Array.from(entitiesMap).map(([id, lastModifiedDate]): Observable<void> => this._preloadEntityIfNotUpToDate$(id, lastModifiedDate))
        );
      }),
      map((): void => void 0)
    );
  }

  private _getEntitiesIdLastModifiedDateMap$(): Observable<IdLastModifiedDateMapType> {
    return this._getEntityIdLastModifiedDateMap$(
      (pageQuery, filtersAndSorts): Observable<IQpConsultationPage<IEntityConsultation>> =>
        this._entityConsultationService.getEntities$(pageQuery, filtersAndSorts),
      [{ type: 'sort', value: 'lastModifiedDate,DESC' }],
      1000 // Limit to 1000 entities to avoid overloading
    );
  }

  private _preloadEntityIfNotUpToDate$(entityId: number, lastModifiedDate: string): Observable<void> {
    return from(this._databaseDataService.getEntity(entityId)).pipe(
      filter((entity: IEntityConsultation | undefined): boolean => {
        // Use the same helper method to extract lastModifiedDate consistently
        const storedLastModifiedDate = entity ? this._extractLastModifiedDate(entity as PreloadItem) : null;
        const isNeedToBePreload = !!(isNil(entity) || storedLastModifiedDate !== lastModifiedDate);

        if (!isNeedToBePreload) {
          this._updatePreloadState(PreloadKey.ENTITY, {
            preloadedItems: this.preloadState$.value.entity.preloadedItems + 1,
          });
        }

        return isNeedToBePreload;
      }),
      switchMap(
        (): Observable<IQpConsultationPage<IEntityConsultation>> =>
          this._entityConsultationService.getEntities$({ page: 0, size: 1 }, [{ type: 'filter', value: `id,EQUALS,${entityId}` }])
      ),
      switchMap((page: IQpConsultationPage<IEntityConsultation>): Observable<IEntityConsultation | undefined> => {
        if (page.items.length > 0) {
          this._updatePreloadState(PreloadKey.ENTITY, {
            preloadedItems: this.preloadState$.value.entity.preloadedItems + 1,
          });

          // Store entity as-is - no need to flatten the structure
          // The database and search will handle the history.lastModifiedDate structure
          const entityToStore = page.items[0];

          return from(this._databaseDataService.saveEntity(entityToStore));
        }

        return of(undefined);
      }),
      map((): void => void 0)
    );
  }

  // PACKING LIST 📦

  private _preloadPackingLists$(): Observable<void> {
    return this._getPackingListsIdLastModifiedDateMap$().pipe(
      take(1),
      switchMap((packinglistMap: IdLastModifiedDateMapType): Observable<(PackingListDTO | undefined)[]> => {
        this._updatePreloadState(PreloadKey.PACKING_LIST, {
          items: packinglistMap.size,
          preloadedItems: 0,
        });

        return combineLatest(
          Array.from(packinglistMap).map(
            ([id, lastModifiedDate]): Observable<PackingListDTO | undefined> => this._preloadPackingListIfNotUpToDate$(id, lastModifiedDate)
          )
        );
      }),
      map((): void => void 0)
    );
  }

  /**
   * @description Here we are getting all the packing having
   * arrivalDate date between one month ago and one month from now
   * and sorting by last modified date
   * @returns {Observable<IdLastModifiedDateMapType>} Observable mapping packing list IDs to their last modified dates
   */
  private _getPackingListsIdLastModifiedDateMap$(): Observable<IdLastModifiedDateMapType> {
    return this._getEntityIdLastModifiedDateMap$(
      (pageQuery, filtersAndSorts): Observable<IQpConsultationPage<PackingListSearchDTO>> =>
        this._entityConsultationService.getPackingLists$(pageQuery, filtersAndSorts),
      [
        {
          type: 'filter',
          key: 'arrivalDate',
          value: `arrivalDate,BETWEEN,[${this._oneMonthAgoDateParams},${this._getOneMonthFromNow},${QpDateService.timezone}]`,
        },
        { type: 'sort', value: 'lastModifiedDate,DESC' },
      ]
    );
  }

  private _preloadPackingListIfNotUpToDate$(packingListId: number, lastModifiedDate: string): Observable<PackingListDTO | undefined> {
    return from(this._databaseDataService.getPackingListById(packingListId)).pipe(
      filter((packingList: PackingListDTO | undefined): packingList is PackingListDTO => {
        const isNeedToBePreload = !!(isNil(packingList) || packingList.lastModifiedDate !== lastModifiedDate);

        if (!isNeedToBePreload) {
          this._updatePreloadState(PreloadKey.PACKING_LIST, {
            preloadedItems: this.preloadState$.value.packingList.preloadedItems + 1,
          });
        }

        return isNeedToBePreload;
      }),
      switchMap((): Observable<PackingListDTO> => this._packingListService.getPackingList$(packingListId)),
      switchMap((packingList: PackingListDTO): Observable<PackingListDTO> => {
        this._updatePreloadState(PreloadKey.PACKING_LIST, {
          preloadedItems: this.preloadState$.value.packingList.preloadedItems + 1,
        });

        return from(this._databaseDataService.savePackingList(packingList));
      })
    );
  }

  private _updatePreloadState(key: PreloadKey, state: Partial<PreloadItemState>): void {
    this.preloadState$.next({
      ...this.preloadState$.value,
      [key]: {
        ...this.preloadState$.value[key],
        ...state,
      },
    });
  }

  private _updateLastSync(): void {
    localStorage.setItem(this.LOCAL_STORAGE_LAST_PRELOADING_TIME_KEY, this._qpDateService.getTimestamp().toString());

    this._getlastSynchronization$()
      .pipe(take(1), untilDestroyed(this))
      .subscribe((lastSynchronization: string): void => {
        this._lastSynchronizationBehaviorSubject.next(lastSynchronization);
      });
  }

  private _getlastSynchronization$(): Observable<string> {
    const lastPreloadingTime = localStorage.getItem(this.LOCAL_STORAGE_LAST_PRELOADING_TIME_KEY);

    if (!lastPreloadingTime) {
      return of('');
    }

    return this._accountService.accountLangKey$().pipe(
      take(1),
      map((langKey: string): string => {
        return this._translateService.instant('isp-inspection-preload.last-synchronisation', {
          time: this._getHumanizeTimeDiff(this._qpDateService.getTimestamp(), parseInt(lastPreloadingTime), langKey),
        });
      })
    );
  }

  private _getHumanizeTimeDiff(currentTimestamp, compareTimestamp, locale: string): string {
    return this._qpDateService.getDateDiffDuration(new Date(currentTimestamp), new Date(compareTimestamp)).locale(locale).humanize();
  }

  private _getEntityIdLastModifiedDateMap$(
    // eslint-disable-next-line rxjs/finnish
    getEntity$: (
      params: { page: number; size: number },
      queryParams: IQpDatatableFilterAndSortParam[]
    ) => Observable<IQpConsultationPage<PreloadItem>>,
    params: IQpDatatableFilterAndSortParam[] = [],
    maxItems: number = MAX_ITEMS_TO_PRELOAD
  ): Observable<IdLastModifiedDateMapType> {
    return getEntity$({ page: 0, size: this._defaultEntityPageSize }, params).pipe(
      switchMap((data: IQpConsultationPage<PreloadItem>): Observable<IQpConsultationPage<PreloadItem>[]> => {
        if (data.pageSize === 1) {
          return of([data]);
        }

        const requestTodo: Observable<IQpConsultationPage<PreloadItem>>[] = [of(data)];

        for (let i = 1; i < data.totalPageCount && i * this._defaultEntityPageSize <= maxItems; i++) {
          requestTodo.push(getEntity$({ page: i, size: this._defaultEntityPageSize }, params));
        }

        return combineLatest(requestTodo);
      }),
      map((data: IQpConsultationPage<PreloadItem>[]): IdLastModifiedDateMapType => {
        const map: IdLastModifiedDateMapType = new Map();

        data.forEach((page: IQpConsultationPage<PreloadItem>): void => {
          page.items.forEach((item: PreloadItem): void => {
            const lastModifiedDate = this._extractLastModifiedDate(item);

            if (isObject(item) && 'id' in item && isNumber(item.id) && lastModifiedDate) {
              map.set(item.id, lastModifiedDate);
            } else {
              console.error(`Cannot preload ${JSON.stringify(item)} because it's not a valid entity - missing id or lastModifiedDate`);
            }
          });
        });

        return map;
      })
    );
  }

  /**
   * Extract lastModifiedDate from different entity structures
   * - Some entities have lastModifiedDate directly (products, workflows, checklists)
   * - Others have it nested in history object (entities)
   * @param item
   */
  private _extractLastModifiedDate(item: PreloadItem): string | null {
    if (!isObject(item)) {
      return null;
    }

    // Direct lastModifiedDate property (products, workflows, checklists, etc.)
    if ('lastModifiedDate' in item && item.lastModifiedDate) {
      return item.lastModifiedDate.toString();
    }

    // Nested in history object (entities)
    if ('history' in item && item.history?.lastModifiedDate) {
      return item.history.lastModifiedDate.toString();
    }

    return null;
  }

  private _endPreloadOperation$<T>(operation: Observable<T>): Observable<T | void> {
    return operation.pipe(
      take(1),
      catchError((err): Observable<void> => {
        console.error(err);

        return of(void 0);
      }),
      defaultIfEmpty(void 0)
    );
  }
}

export enum PreloadKey {
  PRODUCT = 'product',
  PO = 'po',
  INSPECTION_TYPE = 'inspectionType',
  INSPECTION_TYPE_SETTING = 'inspectionTypeSetting',
  WORKFLOW = 'workflow',
  DEFECTS_CHECKLIST = 'defectsChecklist',
  TESTS_CHECKLIST = 'testsChecklist',
  CLIENT = 'client',
  ENTITY = 'entity',
  PACKING_LIST = 'packingList',
}

export type PreloadState = {
  [key in PreloadKey]: PreloadItemState;
};
